"use client";

import { useRef, useEffect, ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface FaqAccordionProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  id?: string;
  badge?: ReactNode;
}

export default function FaqAccordion({
  question,
  answer,
  isOpen,
  onToggle,
  id,
  badge,
}: FaqAccordionProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (isOpen) {
      el.style.maxHeight = el.scrollHeight + "px";
    } else {
      el.style.maxHeight = "0px";
    }
  }, [isOpen]);

  return (
    <div
      className={cn(
        "rounded-xl overflow-hidden transition-all duration-200",
        isOpen
          ? "border border-blue-200 border-l-4 border-l-blue-600 bg-blue-50/40"
          : "border"
      )}
      style={
        isOpen
          ? undefined
          : { borderColor: "var(--color-border)", backgroundColor: "var(--color-bg-card)" }
      }
    >
      {/* Question trigger */}
      <button
        id={id ? `faq-trigger-${id}` : undefined}
        aria-expanded={isOpen}
        aria-controls={id ? `faq-content-${id}` : undefined}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {badge && <span className="shrink-0">{badge}</span>}
          <span
            className="text-sm font-medium leading-snug"
            style={{ color: "var(--color-text-primary)" }}
          >
            {question}
          </span>
        </div>
        <ChevronDown
          className={cn(
            "w-4 h-4 flex-shrink-0 transition-transform duration-300 ease-in-out",
            isOpen ? "rotate-180 text-blue-600" : ""
          )}
          style={isOpen ? undefined : { color: "var(--color-text-muted)" }}
        />
      </button>

      {/* Answer panel — animated via max-height */}
      <div
        ref={contentRef}
        id={id ? `faq-content-${id}` : undefined}
        role="region"
        aria-labelledby={id ? `faq-trigger-${id}` : undefined}
        style={{ maxHeight: "0px", overflow: "hidden" }}
        className="transition-all duration-300 ease-in-out"
      >
        <div
          className="px-4 sm:px-5 pb-5 pt-1 text-sm leading-relaxed prose-sm max-w-none"
          style={{ color: "var(--color-text-secondary)" }}
          dangerouslySetInnerHTML={{ __html: renderAnswer(answer) }}
        />
      </div>
    </div>
  );
}

/**
 * Minimal inline renderer: converts **bold**, *italic*, bullet lists, info blocks (> text),
 * and newlines to HTML. Keeps the component dependency-free (no remark/rehype needed).
 */
function renderAnswer(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const lines = escaped.split("\n");
  const output: string[] = [];
  let inList = false;
  let inTable = false;
  let tableRows: string[] = [];

  const flushList = () => {
    if (inList) {
      output.push("</ul>");
      inList = false;
    }
  };
  const flushTable = () => {
    if (inTable && tableRows.length > 0) {
      // First row is header
      const headerCells = tableRows[0]
        .split("|")
        .filter((c) => c.trim())
        .map((c) => `<th class="px-3 py-1.5 text-left text-xs font-semibold">${c.trim()}</th>`)
        .join("");
      const bodyRows = tableRows
        .slice(2) // skip separator row
        .map((row) => {
          const cells = row
            .split("|")
            .filter((c) => c.trim())
            .map((c) => `<td class="px-3 py-1.5 text-xs">${applyInline(c.trim())}</td>`)
            .join("");
          return `<tr class="border-t border-blue-100">${cells}</tr>`;
        })
        .join("");
      output.push(
        `<div class="overflow-x-auto my-3"><table class="w-full rounded-lg overflow-hidden border border-blue-200 text-left"><thead class="bg-blue-50"><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`
      );
      tableRows = [];
      inTable = false;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    // Table row
    if (line.startsWith("|")) {
      flushList();
      inTable = true;
      tableRows.push(line);
      continue;
    }
    if (inTable) {
      flushTable();
    }

    // Blockquote / info box
    if (line.startsWith("&gt;") || line.startsWith("&gt; ")) {
      flushList();
      const content = applyInline(line.replace(/^&gt;\s?/, ""));
      output.push(
        `<div class="my-2 px-4 py-3 rounded-lg border border-blue-200 bg-blue-50 text-xs text-blue-700 leading-relaxed">${content}</div>`
      );
      continue;
    }

    // Unordered list item
    if (line.match(/^[-*]\s/)) {
      if (!inList) {
        output.push('<ul class="list-disc list-inside space-y-1 my-2 pl-1">');
        inList = true;
      }
      const content = applyInline(line.replace(/^[-*]\s/, ""));
      output.push(`<li class="text-sm leading-snug">${content}</li>`);
      continue;
    }

    flushList();

    // Numbered list item
    if (line.match(/^\d+\.\s/)) {
      const content = applyInline(line.replace(/^\d+\.\s/, ""));
      output.push(`<p class="my-1 pl-4 text-sm">${content}</p>`);
      continue;
    }

    // Heading-style bold line (all bold on its own line)
    if (line.startsWith("**") && line.endsWith("**")) {
      output.push(
        `<p class="mt-3 mb-1 text-sm font-semibold" style="color:var(--color-text-primary)">${applyInline(line)}</p>`
      );
      continue;
    }

    // Empty line → spacing
    if (!line.trim()) {
      output.push('<div class="h-1.5"></div>');
      continue;
    }

    // Regular paragraph
    output.push(`<p class="my-1 text-sm">${applyInline(line)}</p>`);
  }

  flushList();
  flushTable();

  return output.join("");
}

function applyInline(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, '<code class="bg-blue-100 text-blue-800 rounded px-1 py-0.5 text-xs font-mono">$1</code>')
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="text-blue-600 underline hover:text-blue-800">$1</a>'
    );
}
